<template>
  <BaseModal :value="show" @input="$emit('close')">
    <template v-slot:header>
      <div class="text-xl font-bold">Placetopay Payment</div>
    </template>
    <div class="p-6 w-full" style="max-width: 640px; margin: 0 auto;">
      <div v-if="loading" class="text-center py-8">
        <BaseMiniSpinner />
        <p class="text-base text-gray-600 mt-2">Loading payment...</p>
      </div>

      <div
        v-else-if="loadError"
        class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded"
      >
        {{ loadError }}
        <button class="underline ml-2" @click="loadPayment">Retry</button>
      </div>

      <template v-else-if="payment">
        <div class="p-4 bg-gray-50 rounded-lg space-y-1 text-base">
          <div class="flex justify-between">
            <span class="text-gray-600">Internal reference</span>
            <span class="font-mono">{{ payment.internalReference }}</span>
          </div>
          <div v-if="payment.authorization" class="flex justify-between">
            <span class="text-gray-600">Authorization</span>
            <span class="font-mono">{{ payment.authorization }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-600">Date</span>
            <span>{{ payment.date }}</span>
          </div>
          <div v-if="payment.card" class="flex justify-between">
            <span class="text-gray-600">Card</span>
            <span>•••• {{ payment.card }}</span>
          </div>
          <div v-if="payment.cardholderName" class="flex justify-between">
            <span class="text-gray-600">Cardholder</span>
            <span>{{ payment.cardholderName }}</span>
          </div>
          <div class="border-t border-gray-300 my-2" />
          <div class="flex justify-between">
            <span class="text-gray-600">Paid</span>
            <span>{{ money(payment.amount) }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-600">Refunded</span>
            <span :class="{ 'text-red-600': payment.amountRefunded > 0 }">
              {{ money(payment.amountRefunded) }}
            </span>
          </div>
          <div class="flex justify-between font-bold text-base">
            <span>Refundable</span>
            <span>{{ money(payment.refundable) }}</span>
          </div>
        </div>

        <div v-if="payment.refunds.length > 0" class="mt-4">
          <h3 class="font-semibold mb-2">Refunds</h3>
          <div class="table-full">
            <table class="default-table text-base">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Detail</th>
                </tr>
              </thead>
              <tr v-for="refund in payment.refunds" :key="refund.id">
                <td>{{ refund.date }}</td>
                <td>{{ money(refund.amount) }}</td>
                <td class="text-gray-600">{{ refundDetail(refund) }}</td>
              </tr>
            </table>
          </div>
        </div>

        <div v-if="canRefund" class="mt-6 border-t pt-4">
          <h3 class="font-semibold mb-3">Refund</h3>
          <div class="flex gap-4 mb-3 text-base">
            <label class="flex items-center gap-1">
              <input type="radio" value="full" v-model="refundType" />
              Full ({{ money(payment.refundable) }})
            </label>
            <label class="flex items-center gap-1">
              <input type="radio" value="partial" v-model="refundType" />
              Partial
            </label>
          </div>
          <div v-if="refundType === 'partial'" class="mb-3">
            <label class="text-base text-gray-600">Amount</label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              :max="payment.refundable"
              v-model="partialAmount"
              class="w-full border rounded px-3 py-2"
            />
            <p class="text-sm text-gray-500 mt-1">
              Placetopay only accepts partial refunds once the transaction has
              been settled by the bank (usually the next business day). Before
              that, only a full refund is possible.
            </p>
          </div>
          <div class="mb-3">
            <label class="text-base text-gray-600">Internal note</label>
            <textarea
              v-model="note"
              rows="2"
              maxlength="120"
              class="w-full border rounded px-3 py-2"
            />
          </div>

          <div
            class="mb-3 px-3 py-2 rounded text-base bg-gray-50 border border-gray-200 text-gray-700"
          >
            Will be recorded on order #{{ orderNumber }}. The order status and
            stock are not changed.
          </div>

          <div
            v-if="refundError"
            class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-3 text-base"
          >
            {{ refundError }}
          </div>

          <button
            class="w-full bg-red-600 text-white px-6 py-3 rounded-md font-semibold hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="refunding || refundAmount === null"
            @click="submitRefund"
          >
            <span v-if="refunding">Refunding...</span>
            <span v-else-if="refundAmount !== null"
              >Refund {{ money(refundAmount) }}</span
            >
            <span v-else>Enter a valid amount</span>
          </button>
        </div>
        <div
          v-else-if="!hasPermission"
          class="mt-4 text-base text-gray-500 text-center"
        >
          You don't have permission to refund payments.
        </div>
      </template>
    </div>
  </BaseModal>
</template>
<script>
import {
  getPlacetopayPayment,
  refundPlacetopayPayment
} from "@/api/store/PlacetopayRefundsService.js";

function newIdempotencyKey() {
  if (window.crypto && typeof window.crypto.randomUUID === "function") {
    return window.crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 12)}`;
}

// Detalle y reembolso (total o parcial) de un pago de Placetopay de una orden.
// Mismo flujo que StripePaymentDetailModal, sin recibo ni devoluciones (returns).
export default {
  props: {
    show: { type: Boolean, required: true },
    orderNumber: { type: Number, required: true },
    // orders_v2_payments.id del pago de Placetopay
    paymentId: { type: [Number, String], required: true }
  },
  data() {
    return {
      loading: false,
      loadError: null,
      payment: null,
      refundType: "full",
      partialAmount: "",
      note: "",
      refunding: false,
      refundError: null,
      // Una key por intento: un doble click o reintento de red no reembolsa dos veces.
      idempotencyKey: newIdempotencyKey()
    };
  },
  computed: {
    hasPermission() {
      return this.$store.getters["auth/userHasPermission"](
        "can_refund_payment"
      );
    },
    canRefund() {
      return this.hasPermission && this.payment && this.payment.refundable > 0;
    },
    // Monto a reembolsar, o null si el parcial no es válido.
    refundAmount() {
      if (!this.payment) return null;
      if (this.refundType === "full") return this.payment.refundable;
      const cents = Math.round(parseFloat(this.partialAmount) * 100);
      const refundableCents = Math.round(this.payment.refundable * 100);
      if (!cents || cents <= 0 || cents > refundableCents) {
        return null;
      }
      return cents / 100;
    }
  },
  watch: {
    show: {
      immediate: true,
      handler(val) {
        if (val) this.loadPayment();
      }
    }
  },
  methods: {
    money(value) {
      const currency = this.payment?.currency || "";
      return `${currency} ${Number(value || 0).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })}`.trim();
    },
    // more_info: "refund_of:… | key:… | by: Nombre <email> (#id) | nota"
    refundDetail(refund) {
      return String(refund.info || "")
        .split(" | ")
        .filter(part => !/^(refund_of|key):/.test(part))
        .join(" | ");
    },
    errorMessage(err) {
      return err.response?.data?.error ?? err.message;
    },
    async loadPayment() {
      if (!this.hasPermission) {
        this.loadError = "You don't have permission to refund payments.";
        return;
      }
      this.loading = true;
      this.loadError = null;
      try {
        this.payment = await getPlacetopayPayment(
          this.orderNumber,
          this.paymentId
        );
      } catch (err) {
        this.loadError = this.errorMessage(err);
      } finally {
        this.loading = false;
      }
    },
    async submitRefund() {
      if (this.refundAmount === null || this.refunding) return;
      const label = this.refundType === "full" ? "the full remaining " : "";
      if (
        !confirm(
          `Refund ${label}${this.money(
            this.refundAmount
          )} to the customer's card? This cannot be undone.`
        )
      ) {
        return;
      }
      this.refunding = true;
      this.refundError = null;
      try {
        const result = await refundPlacetopayPayment({
          orderNumber: this.orderNumber,
          paymentId: this.paymentId,
          full: this.refundType === "full",
          amount: this.refundType === "partial" ? this.refundAmount : null,
          expectedRefundable: this.payment.refundable,
          note: this.note,
          idempotencyKey: this.idempotencyKey
        });
        const refunded = this.refundAmount;
        this.idempotencyKey = newIdempotencyKey();
        this.payment = result.payment;
        this.refundType = "full";
        this.partialAmount = "";
        this.note = "";
        this.$toast.open({
          message: `Refund approved: ${this.money(refunded)}`,
          type: "success",
          position: "top-right"
        });
        this.$emit("refunded", result.payment);
      } catch (err) {
        this.refundError = this.errorMessage(err);
        // Sin respuesta (timeout/red) el reembolso pudo haberse hecho: se conserva
        // la key para que un reintento no duplique. Con respuesta, el siguiente usa otra.
        if (err.response) {
          this.idempotencyKey = newIdempotencyKey();
        }
        // 409: el pago cambió desde que se cargó; se recarga para decidir de nuevo.
        if (err.response?.status === 409) {
          this.loadPayment();
        }
      } finally {
        this.refunding = false;
      }
    }
  }
};
</script>
